// Public peer network over standard WebTorrent (BitTorrent v1 over WebRTC, WSS tracker).
//
// Rules (Addenda A/B): public eligible resources only (manifest.torrent present), effective hybrid policy + admin
// publicPeerSharing + explicit visitor opt-in; same-origin `/tracker` only; one global upload cap; bounded peers,
// swarms and cached files; leaving is immediate. The torrent's piece store is a bridge onto the engine's own store
// (OPFS), so no second copy of the file is kept in memory, and every piece a peer delivers must also match the
// manifest SHA-256 before it is counted or stored. Rarest-first piece selection comes from WebTorrent itself.
import { activeKeys, createOpfsStore, listCacheEntries, opfsSupported, sweepOpfs } from '../stores/opfs-store.js';
import { digestHex } from '../sha256.js';
import { validateManifest } from '../manifest.js';
import { createRelayHelper } from './relay.js';

export const PEER_LIMITS = Object.freeze({ maxConns: 12, maxCachedSwarms: 3, maxSwarms: 6, torrentMetaBytes: 4 * 1024 * 1024, revalidateMs: 60_000 });

const HEX40 = /^[0-9a-f]{40}$/;

/** Announce URLs accepted by this page: the backend tracker on the page's own host only. */
export function allowedAnnounce(list, loc = globalThis.location) {
  if (!Array.isArray(list) || !loc) return [];
  const want = new Set([`wss://${loc.host}/tracker`]);
  if (loc.protocol === 'http:' && /(^|\.)localhost$|^127\.0\.0\.1$/.test(loc.hostname)) want.add(`ws://${loc.host}/tracker`); // local fixture only
  return list.filter((u) => typeof u === 'string' && want.has(u));
}

/** Check the torrent block of a validated manifest before any network use. Throws with a user-safe message. */
export function checkTorrentBlock(manifest, loc = globalThis.location) {
  const t = manifest && manifest.torrent;
  const bad = (why) => Object.assign(new Error('This file cannot be shared with peers right now.'), { detail: why });
  if (!t || typeof t !== 'object') throw bad('no torrent');
  if (!HEX40.test(String(t.infoHash))) throw bad('infoHash');
  if (t.pieceLength !== manifest.pieceLength) throw bad('piece boundaries differ from HTTPS pieces');
  if (!Array.isArray(t.pieceSha1) || t.pieceSha1.length !== manifest.pieces.length || t.pieceSha1.some((h) => !HEX40.test(String(h)))) throw bad('pieceSha1');
  if (Array.isArray(t.webSeeds) && t.webSeeds.length) throw bad('web seeds are not accepted');
  let announce = allowedAnnounce(t.announce, loc);
  if (!announce.length) {
    // Full-app hosts: the API may advertise its canonical tenant host (e.g. drive-demo) while this page is an alias of
    // the same tenant (drive-upgrade). Every app host proxies its own `/tracker`, so the only tracker ever contacted is
    // this page's same-origin `/tracker`; the advertised host is never connected to.
    if (Array.isArray(t.announce) && t.announce.some(isTrackerUrl)) announce = sameOriginTracker(loc);
  }
  if (!announce.length) throw bad('no same-origin tracker');
  return { infoHash: t.infoHash, announce };
}

function isTrackerUrl(u) {
  try { const x = new URL(String(u)); return (x.protocol === 'wss:' || x.protocol === 'ws:') && x.pathname === '/tracker' && !x.search && !x.username; } catch { return false; }
}

/** The page's own tracker endpoint (wss, or ws for loopback http fixtures only). */
export function sameOriginTracker(loc = globalThis.location) {
  if (!loc) return [];
  if (loc.protocol === 'https:') return [`wss://${loc.host}/tracker`];
  if (loc.protocol === 'http:' && /(^|\.)localhost$|^127\.0\.0\.1$/.test(loc.hostname)) return [`ws://${loc.host}/tracker`];
  return [];
}

// ---------- .torrent metadata: keep the exact info dictionary, replace every tracker with the approved one ----------
const enc = new TextEncoder();
function benEnd(b, i) {
  const c = b[i];
  if (c === 0x69) { const e = b.indexOf(0x65, i); if (e < 0) throw new Error('bencode'); return e + 1; } // i…e
  if (c === 0x6c || c === 0x64) { let j = i + 1; while (b[j] !== 0x65) { if (j >= b.length) throw new Error('bencode'); j = benEnd(b, j); } return j + 1; } // l…e / d…e
  if (c >= 0x30 && c <= 0x39) { const colon = b.indexOf(0x3a, i); if (colon < 0) throw new Error('bencode'); const n = Number(new TextDecoder().decode(b.subarray(i, colon))); if (!Number.isSafeInteger(n) || n < 0 || colon + 1 + n > b.length) throw new Error('bencode'); return colon + 1 + n; }
  throw new Error('bencode');
}
const bstr = (s) => { const x = enc.encode(s); return [enc.encode(`${x.length}:`), x]; };
function concat(parts) { const n = parts.reduce((a, p) => a + p.length, 0), out = new Uint8Array(n); let o = 0; for (const p of parts) { out.set(p, o); o += p.length; } return out; }

/** Byte-exact `info` value of a .torrent file (its SHA-1 is the info hash). */
export function torrentInfoBytes(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (b[0] !== 0x64) throw new Error('bencode');
  let i = 1;
  while (b[i] !== 0x65) {
    const kEnd = benEnd(b, i);
    const colon = b.indexOf(0x3a, i);
    const key = new TextDecoder().decode(b.subarray(colon + 1, kEnd));
    const vEnd = benEnd(b, kEnd);
    if (key === 'info') return b.subarray(kEnd, vEnd);
    i = vEnd;
  }
  throw new Error('no info');
}

/**
 * Rebuild the .torrent with only `announce`/`announce-list` = the approved tracker(s) and the original `info` bytes,
 * so WebTorrent cannot reconnect to a tracker named in raw metadata. The info hash is unchanged by construction.
 */
export function rewriteTorrentAnnounce(bytes, announce) {
  const info = torrentInfoBytes(bytes);
  const list = [enc.encode('l'), ...announce.flatMap((u) => [enc.encode('l'), ...bstr(u), enc.encode('e')]), enc.encode('e')];
  return concat([enc.encode('d'), ...bstr('announce'), ...bstr(announce[0]), ...bstr('announce-list'), ...list, ...bstr('info'), info, enc.encode('e')]);
}

async function sha1Hex(bytes) {
  const d = new Uint8Array(await crypto.subtle.digest('SHA-1', bytes));
  return [...d].map((x) => x.toString(16).padStart(2, '0')).join('');
}

/** Fetch-side guard: the metadata's info dictionary must hash to the manifest's info hash before it is used. */
async function approvedTorrent(meta, infoHash, announce) {
  const info = torrentInfoBytes(meta);
  if ((await sha1Hex(info)) !== infoHash) throw new Error('Peer metadata does not match this file.');
  return rewriteTorrentAnnounce(meta, announce);
}

/** Bitfield bytes (MSB-first, BitTorrent order) for the given verified piece indices. */
export function bitfieldBytes(count, indices) {
  const out = new Uint8Array(Math.ceil(count / 8));
  for (const i of indices) if (i >= 0 && i < count) out[i >> 3] |= 0x80 >> (i & 7);
  return out;
}

/**
 * abstract-chunk-store over the engine bridge. `put` is only reached after WebTorrent's own SHA1 check; the bridge
 * then demands the manifest SHA-256 too. A mismatch fails the store write, which makes WebTorrent drop the swarm;
 * the engine keeps downloading over HTTPS.
 */
export class BridgeStore {
  constructor(chunkLength, { length, bridge, slotForPiece }) {
    this.chunkLength = chunkLength;
    this.length = length;
    this.bridge = bridge;
    this.slotForPiece = slotForPiece || (() => 0);
    this.closed = false;
  }
  put(index, buf, cb = () => {}) {
    if (this.closed) return queueMicrotask(() => cb(new Error('store closed')));
    const bytes = buf instanceof Uint8Array ? new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength) : new Uint8Array(buf);
    Promise.resolve(this.bridge.offer(index, bytes.slice(), this.slotForPiece(index)))
      .then((r) => (r === 'corrupt' ? cb(Object.assign(new Error('Peer data did not match the file fingerprint.'), { solredReason: 'corrupt' }))
        : r === 'stale' ? cb(Object.assign(new Error('Transfer is no longer active.'), { solredReason: 'stale' })) : cb(null)), cb);
  }
  get(index, opts, cb) {
    if (typeof opts === 'function') { cb = opts; opts = null; }
    if (this.closed) return queueMicrotask(() => cb(new Error('store closed')));
    Promise.resolve(this.bridge.get(index)).then((b) => {
      if (!b) return cb(new Error('Piece is not available.'));
      const off = (opts && opts.offset) || 0;
      const len = opts && opts.length != null ? opts.length : b.length - off;
      cb(null, b.subarray(off, off + len));
    }, cb);
  }
  close(cb = () => {}) { this.closed = true; queueMicrotask(() => cb(null)); }
  destroy(cb = () => {}) { this.closed = true; queueMicrotask(() => cb(null)); } // the engine owns the bytes; never deleted here
}

let WebTorrentCtor = null;
async function loadWebTorrent() {
  if (!WebTorrentCtor) WebTorrentCtor = (await import('webtorrent/dist/webtorrent.min.js')).default;
  return WebTorrentCtor;
}

/**
 * @param {object} [o]
 * @param {() => Promise<object>} [o.createClient]  injectable for tests; defaults to a real WebTorrent client
 * @param {RTCIceServer[]} [o.iceServers]  from same-origin runtime config; default: none (no third-party STUN)
 * @param {Location} [o.location]
 * @param {object} [o.cache]  injectable cache storage for tests: { supported, list, open, sweep, activeKeys }
 * @param {number} [o.revalidateMs]  period of the cached-swarm TTL/policy revalidation while consent is on
 * @param {() => number} [o.now]
 */
export function createPeerNetwork(o = {}) {
  const loc = o.location || globalThis.location;
  const cacheIo = { supported: opfsSupported, list: listCacheEntries, open: (m) => createOpfsStore(m, { active: false, touch: false }), sweep: sweepOpfs, activeKeys, ...(o.cache || {}) };
  const now = o.now || (() => Date.now());
  const revalidateMs = o.revalidateMs ?? PEER_LIMITS.revalidateMs;
  let revalTimer = 0, lastApi = null, lastPolicy = null;
  let client = null, clientPromise = null, uploadCap = null;
  const swarms = new Set(); // engine swarms (downloading or seeding the current file)
  const cached = new Map(); // infoHash → cached swarm (previous verified public files)
  let consent = 0, cacheRun = null;

  async function getClient(policy) {
    const cap = Number(policy.peerUploadLimitBytesPerSecond ?? policy.uploadLimitBytesPerSecond);
    uploadCap = Number.isFinite(cap) && cap > 0 ? cap : 262144; // never unlimited
    if (!clientPromise) {
      clientPromise = (async () => {
        if (o.createClient) return o.createClient({ uploadLimit: uploadCap });
        const WebTorrent = await loadWebTorrent();
        if (!WebTorrent.WEBRTC_SUPPORT) throw new Error('This browser cannot connect to peers.');
        return new WebTorrent({ dht: false, lsd: false, utp: false, natUpnp: false, natPmp: false, webSeeds: false, maxConns: PEER_LIMITS.maxConns, uploadLimit: uploadCap, tracker: { rtcConfig: { iceServers: o.iceServers || [] } } });
      })();
      client = await clientPromise;
      client.on?.('error', () => {});
    } else client = await clientPromise;
    client.throttleUpload?.(uploadCap); // one global cap for every swarm on the page
    return client;
  }

  function addTorrent(c, torrentBytes, opts, signal) {
    return new Promise((resolve, reject) => {
      let t;
      const fail = (e) => { clearTimeout(timer); signal?.removeEventListener('abort', cancel); try { t && t.destroy({ destroyStore: false }); } catch { /* gone */ } reject(e); };
      const cancel = () => fail(new Error('Peer join was cancelled.'));
      const timer = setTimeout(() => fail(new Error('Peer metadata did not load in time.')), 15_000);
      if (signal?.aborted) return cancel();
      signal?.addEventListener('abort', cancel, { once: true });
      try {
        t = c.add(torrentBytes, opts);
      } catch (e) { fail(e); return; }
      t.once('error', fail);
      const ok = () => { clearTimeout(timer); signal?.removeEventListener('abort', cancel); resolve(t); };
      if (t.ready) ok(); else t.once('ready', ok);
    });
  }

  /** Wraps a WebTorrent torrent with counters from the actual wire. */
  function wrap(t, { bridge, onChange, onFail, count, kind }) {
    const slots = new Map(); let nextSlot = 0, rejected = 0, blocked = false, left = false;
    const pieceSlot = new Map();
    t.on('wire', (wire) => {
      const slot = nextSlot++ % 250;
      slots.set(wire, slot);
      wire.on?.('piece', (index) => { pieceSlot.set(index, slot); });
      wire.once?.('close', () => { slots.delete(wire); onChange && onChange(); });
      onChange && onChange();
    });
    t.on('warning', (e) => {
      const m = String((e && e.message) || e);
      if (/failed verification/i.test(m)) rejected++;
      if (/tracker|websocket|403|unknown torrent|sharing disabled/i.test(m)) blocked = true;
      onChange && onChange();
    });
    t.on('error', (e) => { if (!left) { left = true; swarms.delete(api); onFail && onFail(e); } });
    t.on('upload', () => onChange && onChange());
    const api = {
      kind,
      infoHash: t.infoHash,
      has: (i) => t.wires.some((w) => !w.peerChoking && w.peerPieces && w.peerPieces.get(i)),
      markVerified(i) {
        if (left || !t.bitfield || t.bitfield.get(i)) return;
        if (typeof t._markVerified === 'function') t._markVerified(i); else t.bitfield.set(i, true);
        t.wires.forEach((w) => { try { w.have(i); } catch { /* closing wire */ } });
        if (typeof t._checkDone === 'function') t._checkDone();
      },
      pause() { if (!left && count) t.deselect(0, count - 1); },
      resume() { if (!left && count) t.select(0, count - 1); },
      stats: () => ({
        activePeers: left ? 0 : t.numPeers,
        peerIds: left ? [] : [...slots.values()].map(String),
        uploadBytesPerSecond: left ? 0 : t.uploadSpeed,
        uploadedBytes: t.uploaded,
        downloadBytesPerSecond: left ? 0 : t.downloadSpeed,
        downloadedBytes: t.downloaded,
        rejectedPieces: rejected,
        blocked: blocked && t.numPeers === 0,
      }),
      leave() {
        if (left) return;
        left = true; swarms.delete(api);
        try { t.destroy({ destroyStore: false }); } catch { /* already gone */ }
        onChange && onChange();
      },
      slotFor: (i) => pieceSlot.get(i) ?? 0,
    };
    return api;
  }

  /**
   * Join the swarm of the file being downloaded/seeded by one engine.
   * @returns {Promise<{has,markVerified,pause,resume,stats,leave}>}
   */
  async function join({ api, session, manifest, policy, bridge, onChange, onFail }) {
    if (!(policy && policy.enabled === true && policy.publicPeerSharing === true)) throw new Error('Peer sharing is turned off for this file.');
    const { infoHash, announce } = checkTorrentBlock(manifest, loc);
    if (swarms.size + cached.size >= PEER_LIMITS.maxSwarms) throw new Error('Too many files are being shared from this page.');
    const old = cached.get(infoHash); // the same file was being seeded from cache: hand over to the engine
    if (old) { old.leave(); cached.delete(infoHash); }
    const raw = await api.torrent(session);
    if (raw.length > PEER_LIMITS.torrentMetaBytes) throw new Error('Peer metadata is too large.');
    const meta = await approvedTorrent(raw, infoHash, announce);
    const c = await getClient(policy);
    const count = manifest.pieces.length;
    let swarm = null;
    const store = new BridgeStore(manifest.pieceLength, { length: manifest.sizeBytes, bridge, slotForPiece: (i) => (swarm ? swarm.slotFor(i) : 0) });
    const t = await addTorrent(c, meta, {
      preloadedStore: store, storeCacheSlots: 0, strategy: 'rarest', announce, private: true,
      bitfield: bitfieldBytes(count, bridge.verifiedIndices()), maxWebConns: 0,
    });
    if (t.infoHash !== infoHash || t.pieces.length !== count || t.pieceLength !== manifest.pieceLength) {
      try { t.destroy({ destroyStore: false }); } catch { /* gone */ }
      throw new Error('Peer metadata does not match this file.');
    }
    swarm = wrap(t, { bridge, onChange, onFail, count, kind: 'engine' });
    swarms.add(swarm);
    return swarm;
  }

  /**
   * After explicit opt-in: also serve verified public pieces of up to 3 previously cached files (this origin's OPFS).
   * Each is revalidated through a fresh session (current version, public eligibility, effective policy) and only
   * complete caches are seeded; nothing unrelated is downloaded. Private/password files never get a torrent.
   */
  /**
   * Re-verify every cached piece against the refreshed manifest SHA-256 before anything is advertised. WebTorrent's
   * startup check samples a single piece, so it is not relied on. Corrupt pieces are dropped and the entry is marked
   * incomplete; it is then skipped (never re-downloaded in the background).
   * @returns {Promise<number[]|null>} verified indices when the whole file is intact, otherwise null
   */
  async function verifyCachedStore(store, m) {
    const have = await store.open();
    if (have.length !== m.pieces.length) return null;
    let bad = 0;
    for (const i of have) {
      if (!consent) return null;
      const b = await store.get(i);
      if (!b || b.length !== m.pieces[i].length || (await digestHex(b)) !== m.pieces[i].sha256) { bad++; await store.drop(i); }
    }
    if (bad) { await store.markIncomplete?.(); return null; }
    return have;
  }

  /** Fresh session for a cached entry: same version + SHA-256, torrent present, effective policy allows sharing. */
  async function revalidateEntry(api, meta) {
    const r = await api.createSession({ resourceId: meta.resourceId });
    const m = validateManifest(r.manifest);
    const pol = r.policy || {};
    if (m.version !== meta.version || m.sha256 !== meta.sha256 || !m.torrent || !(pol.enabled === true && pol.publicPeerSharing === true)) return null;
    const ttl = Number(pol.cacheTtlSeconds);
    if (Number.isFinite(ttl) && ttl > 0 && (now() - (meta.updatedAt || 0)) / 1000 > ttl) return null; // age by last real use
    return { r, m, pol, ttl };
  }

  async function seedCached({ api, exclude = new Set() }) {
    if (!cacheIo.supported()) return;
    const active = cacheIo.activeKeys();
    const entries = (await cacheIo.list()).filter((e) => e.meta && e.meta.complete && e.meta.resourceId && !exclude.has(e.meta.resourceId) && !active.has(e.key));
    entries.sort((a, b) => (b.meta.updatedAt || 0) - (a.meta.updatedAt || 0));
    for (const e of entries) {
      if (!consent || cached.size >= PEER_LIMITS.maxCachedSwarms) break;
      if ([...cached.values()].some((s) => s.key === e.key)) continue;
      try {
        const v = await revalidateEntry(api, e.meta);
        if (!v || !consent) continue;
        const { r, m, pol, ttl } = v;
        const { infoHash, announce } = checkTorrentBlock(m, loc);
        if (cached.has(infoHash) || [...swarms].some((s) => s.infoHash === infoHash)) continue;
        const store = await cacheIo.open(m);
        const have = await verifyCachedStore(store, m);
        if (!have || !consent) continue;
        const bridge = { offer: async () => 'duplicate', get: (i) => store.get(i), verifiedIndices: () => have };
        const meta = await approvedTorrent(await api.torrent({ sessionId: r.sessionId, token: r.token }), infoHash, announce);
        const c = await getClient(pol);
        const t = await addTorrent(c, meta, { preloadedStore: new BridgeStore(m.pieceLength, { length: m.sizeBytes, bridge }), storeCacheSlots: 0, announce, private: true, bitfield: bitfieldBytes(m.pieces.length, have), maxWebConns: 0 });
        if (t.infoHash !== infoHash) { t.destroy({ destroyStore: false }); continue; }
        if (!consent) { t.destroy({ destroyStore: false }); break; }
        const sw = wrap(t, { bridge, count: 0, kind: 'cache' });
        Object.assign(sw, { resourceId: m.resourceId, key: e.key, meta: e.meta, ttl });
        cached.set(infoHash, sw);
      } catch { /* skip this cache entry: not eligible, gone or revoked */ }
    }
  }

  function dropCached(infoHash) { const sw = cached.get(infoHash); if (sw) { sw.leave(); cached.delete(infoHash); } }

  /**
   * Periodic, bounded upkeep while consent is on: leave cached swarms whose TTL passed or whose resource is no longer
   * eligible (version, torrent, policy), apply the cache quota/TTL sweep (never active download keys), leave swarms whose
   * bytes were swept, then fill free slots from other eligible caches.
   */
  async function revalidate(api, policy) {
    if (!consent || !api) return;
    for (const [hash, sw] of [...cached]) {
      if (!consent) return;
      if (Number.isFinite(sw.ttl) && sw.ttl > 0 && (now() - (sw.meta.updatedAt || 0)) / 1000 > sw.ttl) { dropCached(hash); continue; }
      try { if (!(await revalidateEntry(api, sw.meta))) dropCached(hash); } catch { dropCached(hash); }
    }
    if (policy) {
      const { removedKeys = [] } = await cacheIo.sweep({ ttlSeconds: policy.cacheTtlSeconds, quotaBytes: policy.cacheQuotaBytes, now: now() }).catch(() => ({}));
      for (const [hash, sw] of [...cached]) if (removedKeys.includes(sw.key)) dropCached(hash);
    }
    if (consent) await seedCached({ api, exclude: lastExclude });
  }

  let lastExclude = new Set();
  function stopUpkeep() { if (revalTimer) { clearInterval(revalTimer); revalTimer = 0; } }

  /**
   * Partial relay swarm for ONE leased piece of another file (network-relay-http-v1 "Browser boundary"). The torrent
   * starts with nothing selected, then only the leased index is selected; the store accepts that index alone and only
   * when it matches the leased SHA-256, so no other piece is ever kept or advertised. Waits at most `waitMs` for peers.
   * @returns {Promise<{piece: Uint8Array|null, advertise: (bytes: Uint8Array) => void, leave: () => void}|null>}
   */
  async function joinPartial({ torrent, lease, waitMs, signal }) {
    if (!consent || !lastPolicy || signal?.aborted) return null;
    if (swarms.size + cached.size + partials.size >= PEER_LIMITS.maxSwarms) return null;
    if (!(torrent instanceof Uint8Array) || torrent.length > PEER_LIMITS.torrentMetaBytes) return null;
    const announce = sameOriginTracker(loc);
    if (!announce.length) return null;
    const infoHash = await sha1Hex(torrentInfoBytes(torrent));
    if (signal?.aborted || !consent) return null;
    if ([...swarms, ...cached.values(), ...partials].some((s) => s.infoHash === infoHash)) return null; // already served whole
    const meta = rewriteTorrentAnnounce(torrent, announce);
    const c = await Promise.race([getClient(lastPolicy), new Promise((resolve) => signal?.addEventListener('abort', () => resolve(null), { once: true }))]);
    if (!c || signal?.aborted || !consent) return null;
    let piece = null, arrived = null, left = false, publish = null;
    const got = new Promise((resolve) => { arrived = resolve; });
    const bridge = {
      // WebTorrent marks a piece verified and sends `have` as soon as this store write succeeds, so a peer-delivered
      // piece is held here until the server has confirmed it (advertise) and fails otherwise (leave): nothing is
      // published before the confirmation.
      async offer(i, bytes) {
        if (left || i !== lease.index || bytes.length !== lease.length) return 'stale';
        if (piece) return 'duplicate';
        if ((await digestHex(bytes)) !== lease.sha256) return 'corrupt';
        if (left) return 'stale';
        piece = bytes; arrived();
        return new Promise((resolve) => { publish = resolve; });
      },
      get: (i) => (i === lease.index ? piece : null),
      verifiedIndices: () => [],
    };
    const store = new BridgeStore(0, { length: 0, bridge });
    const t = await addTorrent(c, meta, { preloadedStore: store, storeCacheSlots: 0, announce, private: true, deselect: true, maxWebConns: 0, strategy: 'rarest' }, signal);
    if (signal?.aborted || !consent || t.infoHash !== infoHash || !t.pieces || lease.index >= t.pieces.length || t.pieces[lease.index].length !== lease.length) {
      try { t.destroy({ destroyStore: false }); } catch { /* gone */ }
      return null;
    }
    store.chunkLength = t.pieceLength; store.length = t.length;
    const sw = wrap(t, { bridge, count: 0, kind: 'relay' });
    partials.add(sw);
    const leave = sw.leave;
    sw.leave = () => { left = true; partials.delete(sw); publish?.('stale'); publish = null; leave(); };
    signal?.addEventListener('abort', () => { if (!sw.advertised) sw.leave(); }, { once: true });
    if (signal?.aborted) { sw.leave(); return null; }
    t.select(lease.index, lease.index);
    let timer = 0;
    await Promise.race([got, new Promise((resolve) => { timer = setTimeout(resolve, Math.max(0, waitMs)); signal?.addEventListener('abort', resolve, { once: true }); })]);
    clearTimeout(timer);
    if (left) return null;
    if (!piece) t.deselect(lease.index, lease.index); // the origin fill takes over; no background peer download
    return {
      infoHash,
      get piece() { return piece; },
      /** Serve the verified leased piece (peer- or origin-filled) and nothing else. */
      advertise(bytes) {
        if (left || bytes.length !== lease.length) return;
        sw.advertised = true;
        try { t.deselect(lease.index, lease.index); } catch { /* gone */ }
        if (publish) { publish('ok'); publish = null; return; } // the held peer write completes: WebTorrent announces it
        piece = piece || bytes;
        sw.markVerified(lease.index);
      },
      leave: () => sw.leave(),
    };
  }

  const partials = new Set(); // relay swarms holding single leased pieces of other files
  let relay = null; // page-level relay helper (one leader tab per origin)
  const relayTargets = []; // consenting engines' relay targets, oldest first

  return {
    join,
    joinPartial,
    /** Page-level consent (any engine opted in). Turning it off stops every cached swarm immediately. */
    setConsent(on, { api, exclude, policy, relay: relayTarget } = {}) {
      consent = Math.max(0, consent + (on ? 1 : -1));
      if (!on && relayTarget) {
        // Each engine owns its relay target: the helper follows the most recent engine that still consents.
        const at = relayTargets.indexOf(relayTarget);
        if (at >= 0) relayTargets.splice(at, 1);
        // Always re-select the latest consenting target: this also supersedes a start of the withdrawn target that
        // may still be queued behind an earlier cleanup.
        if (relay && consent) { const next = relayTargets.at(-1); void (next ? relay.start(next) : relay.stop()); }
      }
      if (!consent) { relayTargets.length = 0; void relay?.stop(); stopUpkeep(); for (const s of [...cached.values(), ...partials]) s.leave(); cached.clear(); partials.clear(); return; }
      if (!on || !api) return;
      lastApi = api; lastPolicy = policy || lastPolicy; lastExclude = new Set([...lastExclude, ...(exclude || [])]);
      // Cross-file relay needs the operator's explicit cross-file sharing and a managed, admitted foreground session.
      if (relayTarget && o.relay !== false && lastPolicy?.crossFileSharingEnabled === true && lastPolicy?.managedNetworkEnabled === true) {
        relay = relay || createRelayHelper({ api, joinPartial, ...(o.relay || {}) });
        if (!relayTargets.includes(relayTarget)) relayTargets.push(relayTarget);
        void relay.start(relayTarget);
      }
      const run = () => {
        if (cacheRun) return cacheRun;
        cacheRun = revalidate(lastApi, lastPolicy).catch(() => {}).finally(() => { cacheRun = null; });
        return cacheRun;
      };
      void run();
      if (!revalTimer && revalidateMs > 0) revalTimer = setInterval(run, revalidateMs);
    },
    /** Run one upkeep pass now (tests; also safe to call from hosts). */
    upkeep: () => (cacheRun || revalidate(lastApi, lastPolicy)),
    stats() {
      let up = 0, peers = 0;
      for (const s of [...swarms, ...cached.values(), ...partials]) { const st = s.stats(); up += st.uploadBytesPerSecond || 0; peers += st.activePeers || 0; }
      return { uploadBytesPerSecond: up, uploadLimitBytesPerSecond: uploadCap, activeSwarms: swarms.size + cached.size + partials.size, cachedSwarms: cached.size, relaySwarms: partials.size, activePeers: peers };
    },
    cachedResources: () => [...cached.values()].map((s) => s.resourceId),
    relayStats: () => (relay ? relay.stats() : null),
    destroy() { relay?.destroy(); relay = null; relayTargets.length = 0; stopUpkeep(); consent = 0; for (const s of [...swarms, ...cached.values(), ...partials]) s.leave(); swarms.clear(); cached.clear(); partials.clear(); try { client && client.destroy(); } catch { /* gone */ } client = null; clientPromise = null; },
  };
}
